import { SettingsName } from "@app/class/SettingsName";
import { SensorDataValueType } from "@app/interfaces/ISensorDataValue";
import { ReasonCode } from "@enums/ReasonCode";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { FoundationPeripheralService } from "@foundation/_services/peripheral.service.ajs";
import { IPlot, IPlotTemperatureRecord } from "@interfaces/IPlot";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { AccountFeatureService } from "@services/accountFeature.service";
import { AccountOption } from "@services/class/AccountOption";
import { ConstantsService } from "@services/constantsService";
import { Debounce } from "@services/debounce.service";
import { PlotService } from "@services/plot.service";
import { ReasonCodeService } from "@services/reasonCode.service";
import { SessionService } from "@services/session.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import * as moment from "moment";
import { CameraFootageService } from "../videos/services/camera-footage.service";
import { AccountSettingsService, IAccountSettingsContent } from "@services/account-settings.service";
import "./plotInfoTable.scss";
import { SensorDataService } from "@app/main/modules/maintenance/_components/sensorData/sensor-data.service";

interface IPlotInfoTableComponent {
    vehicle: IVehicle;
    plot: IPlot;
    showLatestData: boolean;
    hideVideoButton: boolean;
}

class PlotInfoTableController implements angular.IComponentController, IPlotInfoTableComponent {
    vehicleView: boolean;
    odometer: string;

    plotReasonString: string;
    showGeofenceIcon: boolean;

    vehicleDirection: number;
    vehicleDirectionString: string;

    // -- Input

    vehicle: IVehicle;
    plot: IPlot;
    hideVideoButton: boolean;
    showLatestData: boolean;

    // --

    displayTemperatures: IPlotTemperatureRecord[] = [];

    canEditOdometer: boolean;

    plotReason: ReasonCode;

    currentUser: IUser;

    mapImageUrl: string;
    imageUrl: string;
    hideVideo: boolean;

    accountSettings: IAccountSettingsContent;

    onRequestVideoClick: (args: { requestTime: moment.Moment, $event: MouseEvent }) => void;

    debouncedTemperatureRecordsFn: Function;

    static $inject = ["$rootScope", "veriDate", "debounceService", "vehicleService", "accountFeatureService", "unitConversionService", "plotService", "reasonCodeService", "sessionService", "foundationPeripheralService", "constantsService", "sensorDataService", "cameraFootageService", "accountSettingsService"];

    constructor(
        private readonly $rootScope: IRunBlockRootScope,
        private readonly veriDate: VeriDate,
        private readonly debounceService: Debounce,
        private readonly vehicleService: VehicleService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly unitConversionService: UnitConversionService,
        private readonly plotService: PlotService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly sessionService: SessionService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly constantsService: ConstantsService,
        private readonly sensorDataService: SensorDataService,
        private readonly cameraFootageService: CameraFootageService,
        private readonly accountSettingsService: AccountSettingsService,



    ) {
        this.currentUser = angular.copy(this.$rootScope.currentUser);

        this.getAccountOptions();
    }

    $onInit(): void {

        this.accountSettingsService.getAccountSettings().then(settings => {
            this.accountSettings = settings;
        });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        this.createDebouncedTemperatureRecordsFn();
        this.updateHideVideoButton();

        if (onChangesObj["vehicle"] && this.vehicle) {
            this.foundationPeripheralService.getPeripherals(this.vehicle.Id).then(peripherals => {
                this.vehicle.hasRequestFootageFunctionality = this.cameraFootageService.isFootageFunctionalityAvailable(peripherals);
            });

        }

        if (onChangesObj["vehicle"] || onChangesObj["plot"]) {
            this.updateStrings();
            this.setDisplayTemps();
        }
    }

    requestVideoClicked($event: MouseEvent): void {
        if (this.onRequestVideoClick) {
            this.onRequestVideoClick({ requestTime: this.plot.UtcMoment, $event: $event });
        }
    }

    get accountSettingsShowNonEventFootage(): boolean {
        return !this.accountSettings?.DisableNonEventDownloads?.Value
       
    }

    // -----------------------

    private updateHideVideoButton(): void {    
        if (this.hideVideoButton) {
            this.hideVideo = true;
            return;
        }

        this.hideVideo = !this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Camera) ||
            this.plot?.videoRestricted === true ||
            this.constantsService.getIsMobile();
    }

    private updateStrings() {
        this.showGeofenceIcon = false;

        if (this.plot == null) {
            this.plot = this.vehicle.Plot;
        }

        if (this.plot == null) {
            this.vehicleDirection = 0;
            this.odometer = "N/A";
            this.plotReasonString = "N/A";

            return;
        }

        this.vehicleView = this.vehicle.Plot === this.plot;

        this.vehicleDirection = this.plot.Direction;
        this.plotReasonString = this.reasonCodeService.getReasonCodeLabel(this.plot.Reason);

        this.vehicleDirectionString = this.plotService.convertDirectionToWords(this.vehicleDirection);

        if (this.plot?.LocationInformation?.GeofenceIds?.length > 0) {
            this.showGeofenceIcon = true;
        }

        this.plotReason = this.plot.Reason || null;
        if (this.plotReason === ReasonCode.Temperature && !this.plot.IsIgnitionOn && this.plot.Velocity === 0) {
            this.plotReason = ReasonCode.Wakeup;
        }

        this.getOdometerData();
    }

    private getOdometerData() {
        // convert to km
        this.setOdometer(Math.round(this.plot.Odometer / 1000));

        if (this.showLatestData) {
            this.sensorDataService.getLatestSensorDataValues({ VehicleId: this.plot.VehicleId }).then(latestSensorDataValues => {
                if (!latestSensorDataValues.length) {
                    return;
                }

                this.setOdometer(Math.round(latestSensorDataValues[0].getValue<number>(SensorDataValueType.OdometerKmCan)));
            })
        } else {
            this.setOdometer(Math.round(this.plot.SensorData?.OdometerKm));
        }
    }

    private setOdometer(odometerKm: number): void {
        if (odometerKm == null || odometerKm <= 0 || isNaN(odometerKm)) {
            return;
        }

        this.odometer = this.unitConversionService.convertUnitWithSuffix(odometerKm, UnitConversionType.Distance);
    }

    private getAccountOptions() {
        const accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);
        if (accountOptions) {
            this.canEditOdometer = accountOptions.EditOdometer;
        }
    }

    private setDisplayTemps() {
        this.debouncedTemperatureRecordsFn();
    }

    private createDebouncedTemperatureRecordsFn(): void {
        if (this.debouncedTemperatureRecordsFn) {
            return;
        }

        this.debouncedTemperatureRecordsFn = this.debounceService.debounce(() => {
            this.displayTemperatures = [];

            if (!this.vehicle || !this.plot) {
                return;
            }

            if (this.showLatestData && (this.plot?.TemperatureRecords == null || this.plot?.TemperatureRecords?.length == 0)) {
                this.vehicleService.getLatestVehicleTemperatureRecordsForVehicle(this.vehicle.Id).then(records => {

                    records?.forEach(record => {
                        if (this.veriDate.createMoment(record.Timestamp).isAfter(this.veriDate.createMoment(new Date()).subtract(1, 'hour'))) {
                            this.displayTemperatures.push(record);
                        }
                    });
                })
            }
            else {
                this.displayTemperatures = this.plot.TemperatureRecords;
            }
        }, 100, false);
    }
}

export class PlotInfoTableComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;
    transclude: boolean;

    constructor() {
        this.bindings = {
            vehicle: "<",
            plot: "<",
            hideVideoButton: "<",
            showLatestData: "<",
            onRequestVideoClick: "&",
        };

        this.transclude = true;

        this.controller = PlotInfoTableController;
        this.template = require("./plotInfoTable.template.html");
    }
}
